import type { Currency } from '../locale/currencies';
import { ImpactType } from '@prisma/client';
import Decimal from 'decimal.js';
import { calculateImpactFundUnitPrices } from './impact-fund-pricing';
import { impactShopCurrencyPricesSchema } from '../impact-products/impact-product-schemas';
import type { z } from 'zod';

type CurrencyPrices = Record<Currency, string | number>;

export type UnitPriceRecord<TValue> = {
  gbpUnitPrice: TValue;
  usdUnitPrice: TValue;
  eurUnitPrice: TValue;
  cadUnitPrice: TValue;
  audUnitPrice: TValue;
};

export function mapUnitPriceRecord<TValue, TMappedValue>(
  prices: UnitPriceRecord<TValue>,
  mapValue: (value: TValue) => TMappedValue,
): Record<Currency, TMappedValue> {
  return {
    GBP: mapValue(prices.gbpUnitPrice),
    USD: mapValue(prices.usdUnitPrice),
    EUR: mapValue(prices.eurUnitPrice),
    CAD: mapValue(prices.cadUnitPrice),
    AUD: mapValue(prices.audUnitPrice),
  };
}

export function mapNullableUnitPriceRecord<TValue, TMappedValue>(
  prices: UnitPriceRecord<TValue | null>,
  mapValue: (value: TValue) => TMappedValue,
): Record<Currency, TMappedValue> | null {
  const { gbpUnitPrice, usdUnitPrice, eurUnitPrice, cadUnitPrice, audUnitPrice } = prices;
  if (
    gbpUnitPrice === null ||
    usdUnitPrice === null ||
    eurUnitPrice === null ||
    cadUnitPrice === null ||
    audUnitPrice === null
  ) {
    return null;
  }

  return mapUnitPriceRecord({ gbpUnitPrice, usdUnitPrice, eurUnitPrice, cadUnitPrice, audUnitPrice }, mapValue);
}

export type EffectiveAllocationPriceInput<TCurrencyPrices extends CurrencyPrices = CurrencyPrices> = {
  percentage: string | number;
  inheritProjectPrice: boolean;
  allocationUnitPrices: TCurrencyPrices | null;
  projectUnitPrices: TCurrencyPrices | null;
};

export function resolveEffectiveAllocationUnitPrices<TCurrencyPrices extends CurrencyPrices>(
  allocation: Omit<EffectiveAllocationPriceInput<TCurrencyPrices>, 'percentage'>,
): TCurrencyPrices | null {
  return allocation.inheritProjectPrice ? allocation.projectUnitPrices : allocation.allocationUnitPrices;
}

export function calculateEffectiveImpactFundUnitPrices(
  allocations: EffectiveAllocationPriceInput[],
): z.infer<typeof impactShopCurrencyPricesSchema> | null {
  if (allocations.length === 0) return null;

  const effectiveAllocations: Array<{ percentage: string | number; unitPrices: CurrencyPrices }> = [];
  for (const allocation of allocations) {
    const unitPrices = resolveEffectiveAllocationUnitPrices(allocation);
    if (!unitPrices) return null;
    effectiveAllocations.push({ percentage: allocation.percentage, unitPrices });
  }

  return calculateImpactFundUnitPrices(effectiveAllocations);
}

export function calculateEffectiveMinimumContribution(
  configuredMinimumMinor: number,
  allocations: Array<{
    percentage: Decimal.Value;
    impactType: ImpactType;
    unitPriceMinor: Decimal.Value;
  }>,
): number {
  return allocations.reduce((minimum, allocation) => {
    const baseUnitsPerUnit =
      allocation.impactType === ImpactType.TREES
        ? 1
        : allocation.impactType === ImpactType.HABITAT_RESTORATION
        ? 10_000
        : 1_000_000;
    const minimumAllocationMinor = Decimal.max(
      1,
      new Decimal(allocation.unitPriceMinor).div(baseUnitsPerUnit).times(0.5).ceil(),
    );
    const minimumTotalMinor = minimumAllocationMinor.div(allocation.percentage).ceil().toNumber();
    return Math.max(minimum, minimumTotalMinor);
  }, configuredMinimumMinor);
}
